import { RefObject, useEffect, useState } from "react"

type OnIntersect = (isIntersecting: boolean) => void

interface ObserverRegistry {
   observer: IntersectionObserver
   callbacks: Map<Element, OnIntersect>
}

const registriesByRootMargin = new Map<string, ObserverRegistry>()

const getRegistry = (rootMargin: string): ObserverRegistry => {
   const existing = registriesByRootMargin.get(rootMargin)
   if (existing) return existing

   const callbacks = new Map<Element, OnIntersect>()
   const observer = new IntersectionObserver(
      (entries) => {
         entries.forEach((entry) => {
            callbacks.get(entry.target)?.(entry.isIntersecting)
         })
      },
      { rootMargin }
   )

   const registry = { observer, callbacks }
   registriesByRootMargin.set(rootMargin, registry)

   return registry
}

export function useIsOnScreen(
   ref: RefObject<Element | null>,
   rootMargin = "200px"
): boolean {
   const [isOnScreen, setIsOnScreen] = useState(false)

   useEffect(() => {
      const element = ref.current
      if (!element) return

      const { observer, callbacks } = getRegistry(rootMargin)
      callbacks.set(element, setIsOnScreen)
      observer.observe(element)

      return () => {
         observer.unobserve(element)
         callbacks.delete(element)
      }
   }, [ref, rootMargin])

   return isOnScreen
}

export const resetSharedIntersectionObservers = () => {
   registriesByRootMargin.forEach(({ observer }) => observer.disconnect())
   registriesByRootMargin.clear()
}
